import {
  unstable_createMuiStrictModeTheme as createTheme,
  Shadows,
} from "@mui/material/styles";
import { red } from "@mui/material/colors";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    body3: React.CSSProperties;
    date: React.CSSProperties;
  }
  interface Palette {
    alertInfo: AlertInfo;
  }

  interface AlertInfo {
    main: string;
    background: string;
  }

  // allow configuration using `createTheme()`
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
    date?: React.CSSProperties;
    profile?: React.CSSProperties;
  }
  interface PaletteOptions {
    alertInfo: AlertInfo;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    tile: true;
    feedback: true;
    info: true;
    body3: true;
    date: true;
    profile: true;
  }
}

declare module "@mui/material/Alert" {
  interface AlertPropsVariantOverrides {
    feedback: true;
    info: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    coming: true;
    new: true;
    offline: true;
  }
}

export const bottomNavigationHeight = "80px";

// Create a theme instance.
export const theme = createTheme({
  palette: {
    common: {
      black: "#000000",
      white: "#FFFFFF",
    },
    primary: {
      main: "#A20066",
      dark: "#590038",
    },
    error: {
      main: red.A400,
    },
    background: {
      default: "#F6F6F6",
    },
    text: {
      primary: "#000000",
      secondary: "#494949",
    },
    alertInfo: {
      main: "#121C42",
      background: "#F3F7FB",
    },
    divider: "#EEEEEE",
  },
  typography: {
    fontFamily: "FS Elliot Pro",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h3: {
      fontSize: "1.5rem",
      lineHeight: "1.75rem",
      fontWeight: 900,
      display: "flex",
      alignItems: "center",
    },
    h4: {
      fontSize: "1.25rem",
      lineHeight: "1.625rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.125rem",
      lineHeight: "1.5rem",
      fontWeight: 700,
    },
    body1: {
      fontSize: "1.125rem",
      lineHeight: "1.6875rem",
      fontWeight: 400,
    },
    body2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      fontWeight: 400,
    },
    body3: {
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      fontWeight: 400,
    },
    date: {
      fontSize: "0.75rem",
      lineHeight: "1.125rem",
      fontWeight: 400,
    },
    profile: {
      fontSize: "1rem",
      lineHeight: "1.25rem",
      fontWeight: 700,
      display: "block",
    },
  },
  shadows: [
    "none",
    "0px 2px 12px 0px #0000001A",
    ...Array(23).fill("none"),
  ] as Shadows,
  components: {
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: bottomNavigationHeight,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          paddingBottom: "5px",
        },
        label: {
          fontSize: "0.625rem",
          lineHeight: "1.25rem",
          textTransform: "capitalize",
          fontWeight: 700,
          "&.Mui-selected": {
            fontSize: "0.625rem",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: "tile" },
              style: {
                textDecoration: "none",
                focus: "#AFAFAF",
                ":focus": {
                  backgroundColor: "#AFAFAF1F",
                },
                ":focus-visible": {
                  outline: 0,
                },
              },
            },
          ],
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: "768px",
          padding: "10px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          overflowY: "auto",
          overflowX: "hidden",
          "&.MuiContainer-maxWidthLg": {
            maxWidth: "768px",
            padding: "10px 16px",
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderColor: "#AFAFAF",
        },
      },
    },
    MuiListItemSecondaryAction: {
      styleOverrides: {
        root: {
          display: "flex",
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          background: "transparent",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          textDecoration: "none",
          border: "1px solid white",
          WebkitTapHighlightColor: "transparent",
        },
        img: {
          width: "105%",
          height: "105%",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "42px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "1.125rem",
          fontWeight: 700,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          display: "block",
          margin: "auto",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          marginTop: "4px",
          padding: "0 4px",
          fontSize: "0.75rem",
          fontWeight: 700,
          height: "auto",
          variants: [
            {
              props: { variant: "new" },
              style: {
                color: "#004F0D",
                backgroundColor: "#E6F4E8",
              },
            },
            {
              props: { variant: "coming" },
              style: {
                color: "#2400FF",
                backgroundColor: "#DBE5F2",
              },
            },
            {
              props: { variant: "offline" },
              style: {
                color: "#A20066",
                backgroundColor: "#FDECF2",
              },
            },
          ],
        },
        label: {
          padding: "0 0 0 4px",
        },
        icon: {
          margin: 0,
          fill: "inherit"
        }
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "28px",
          marginBottom: "10px"
        },
        indicator: {
          display: "none",
        }
      }
    }
  },
});

theme.components.MuiTab = {
  styleOverrides: {
    root: {
      opacity: 1,
      textTransform: "capitalize",
      fontSize: "0.75rem",
      fontWeight: 700,
      padding: 0,
      color: theme.palette.text.secondary,
      borderRadius: "8px",
      minHeight: "28px",
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,},
    },
  },
}

theme.components.MuiAlertTitle = {
  styleOverrides: {
    root: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      fontWeight: 700,
      marginTop: 0,
      variants: [
        {
          props: { variant: "info" },
          style: {
            color: theme.palette.alertInfo.main,
          },
        },
        {
          props: { variant: "feedback" },
          style: {
            color: theme.palette.primary.main,
          },
        },
      ],
    },
  },
};

theme.components.MuiAlert = {
  styleOverrides: {
    root: {
      backgroundColor: "#E2F2EF",
      borderRadius: "6px",
      padding: "16px",
      color: theme.palette.text.secondary,
      boxShadow: theme.shadows[1],
      variants: [
        {
          props: { variant: "info" },
          style: {
            backgroundColor: theme.palette.alertInfo.background,
          },
        },
      ],
    },
    icon: {
      padding: 0,
      opacity: 1,
      variants: [
        {
          props: { variant: "feedback" },
          style: {
            color: theme.palette.primary.main,
          },
        },
        {
          props: { variant: "info" },
          style: {
            color: theme.palette.alertInfo.main,
          },
        },
      ],
    },
    message: {
      padding: 0,
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
    },
    action: {
      padding: 0,
      marginRight: 0,
    },
  },
};

theme.components.MuiListItemButton = {
  styleOverrides: {
    root: {
      ":hover": {
        backgroundColor: "inherit",
        color: theme.palette.primary.main,
        fontWeight: 700,
      },
      "&.Mui-selected": {
        backgroundColor: "inherit",
        color: theme.palette.primary.main,
        fontWeight: 700,
      },
      "&.Mui-selected:hover": {
        backgroundColor: "inherit",
      },
    },
  },
};

theme.components.MuiModal = {
  styleOverrides: {
    root: {
      background: theme.palette.common.white,
    },
  },
};

theme.components.MuiMobileStepper = {
  styleOverrides: {
    root: {
      margin: "auto",
    },
    dotActive: {
      backgroundColor: theme.palette.text.secondary,
    },
  },
};
